import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context';
import {
  StripePricingTableEmbed,
  type StripePricingTableConfig,
} from './StripePricingTableEmbed';

type ConfigStatus =
  | { kind: 'loading' }
  | { kind: 'ready'; config: StripePricingTableConfig }
  | { kind: 'error'; message: string };

/**
 * Public pricing page.
 * Logged-in users get a live Stripe Pricing Table (with client reference + email).
 * Logged-out users see the table in view-only mode and are asked to sign in first.
 */
const PricingPage = () => {
  const { user, status: userStatus } = useUser();
  const [configStatus, setConfigStatus] = useState<ConfigStatus>({ kind: 'loading' });

  const isLoggedIn = userStatus === 'ready' && user !== null;

  useEffect(() => {
    if (userStatus === 'loading') return;

    let cancelled = false;
    setConfigStatus({ kind: 'loading' });

    void (async () => {
      try {
        const res = await fetch('/api/billing/config', { credentials: 'include' });
        const data = (await res.json().catch(() => null)) as
          | (StripePricingTableConfig & { error?: string })
          | null;

        if (cancelled) return;

        if (!res.ok || !data?.pricingTableId || !data?.publishableKey) {
          setConfigStatus({
            kind: 'error',
            message: data?.error ?? `Could not load pricing (${res.status})`,
          });
          return;
        }

        setConfigStatus({
          kind: 'ready',
          config: {
            pricingTableId: data.pricingTableId,
            publishableKey: data.publishableKey,
          },
        });
      } catch {
        if (!cancelled) {
          setConfigStatus({
            kind: 'error',
            message: 'Could not load pricing',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.stripeCustomerId, userStatus]);

  return (
    <main className="relative z-0 mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="mb-8 max-w-2xl">
        <p className="inline-flex rounded-full bg-skysoft/50 px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-ink/70">
          Plans for schools
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl">
          Pricing
        </h1>
        <p className="mt-4 font-sans text-lg text-ink/70">
          Log in and start for free — up to 5 students with no paid plan. Upgrade
          whenever you need room for more students. Paid plans include 30 days free,
          and you can cancel anytime.
        </p>
      </div>

      {userStatus === 'loading' && (
        <p className="font-sans text-sm text-ink/60" role="status">
          Loading…
        </p>
      )}

      {userStatus === 'ready' && !isLoggedIn && (
        <div
          className="mb-6 rounded-2xl border-2 border-skysoft bg-skysoft/30 px-4 py-4 font-sans text-sm text-ink"
          role="status"
        >
          <p className="font-semibold">Log in to use OnTime for free</p>
          <p className="mt-1 text-ink/75">
            Create an account and start checking students in with the free tier
            (up to 5 students). Upgrade later when you need a higher student limit —
            you can also browse paid plans below after you sign in.
          </p>
          <Link
            to="/login"
            className="mt-3 inline-flex rounded-full bg-skysoft/80 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft"
          >
            Go to Login
          </Link>
        </div>
      )}

      {configStatus.kind === 'loading' && (
        <p className="font-sans text-sm text-ink/60" role="status">
          Loading plans…
        </p>
      )}

      {configStatus.kind === 'error' && (
        <p
          className="rounded-2xl bg-doghouse/25 px-4 py-3 font-sans text-sm text-ink"
          role="alert"
        >
          {configStatus.message}
        </p>
      )}

      {configStatus.kind === 'ready' && userStatus === 'ready' && (
        <section
          className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
          aria-label="Subscription plans"
        >
          <div
            className="mb-6 rounded-2xl border-2 border-ink/15 bg-skysoft/40 px-4 py-4 sm:px-5"
            role="note"
          >
            <p className="font-display text-base font-semibold text-ink">
              Secure payments with Stripe
            </p>
            <p className="mt-1.5 font-sans text-sm leading-relaxed text-ink/75">
              We use Stripe for all billing. We do not store any payment information on our
              servers — card details and subscriptions are handled entirely by Stripe. You can
              cancel anytime.
            </p>
          </div>
          <StripePricingTableEmbed
            config={configStatus.config}
            clientReferenceId={isLoggedIn ? user?.id?.trim() || undefined : undefined}
            customerEmail={isLoggedIn ? user?.email?.trim() || undefined : undefined}
            interactive={isLoggedIn}
          />
          {!isLoggedIn && (
            <p className="mt-4 text-center font-sans text-sm text-ink/60">
              Plan buttons are disabled until you sign in.
            </p>
          )}
        </section>
      )}
    </main>
  );
};

export default PricingPage;
